import { useState, type FormEvent } from 'react';
import type {
  AgreementLine,
  BillingFrequency,
  CreateAgreementLineInput,
  Product,
} from '@catalyst-psa/shared';
import { BILLING_FREQUENCIES } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import { formatDate } from '../../utils/date.js';
import { useProductOptions } from '../products/api.js';
import { ProductPicker } from '../products/ProductPicker.js';
import {
  useAddCatPlusLines,
  useAgreementLines,
  useCreateAgreementLine,
  useDeleteAgreementLine,
  useReorderAgreementLines,
  useUpdateAgreementLine,
} from './api.js';

interface AgreementLinesSectionProps {
  agreementId: string;
  canEdit: boolean;
  /** Shows the "Add Cat+ lines" button (managed services agreements only). */
  showCatPlus?: boolean;
}

interface LineFormState {
  productId: string;
  description: string;
  billingFrequency: BillingFrequency;
  quantity: string;
  unitPrice: string;
  startDate: string;
  endDate: string;
}

const EMPTY_LINE: LineFormState = {
  productId: '',
  description: '',
  billingFrequency: 'monthly',
  quantity: '1',
  unitPrice: '0',
  startDate: '',
  endDate: '',
};

function toFormState(line: AgreementLine): LineFormState {
  return {
    productId: line.productId ?? '',
    description: line.description,
    billingFrequency: line.billingFrequency,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    startDate: line.startDate,
    endDate: line.endDate ?? '',
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

const FREQUENCY_LABELS: Record<BillingFrequency, string> = {
  monthly: 'Monthly',
  yearly: 'Yearly',
};

const FREQUENCY_OPTIONS = BILLING_FREQUENCIES.map((value) => ({
  value,
  label: FREQUENCY_LABELS[value],
}));

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function AgreementLinesSection({
  agreementId,
  canEdit,
  showCatPlus = false,
}: AgreementLinesSectionProps): JSX.Element {
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<LineFormState>(EMPTY_LINE);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const linesQuery = useAgreementLines(agreementId);
  const productsQuery = useProductOptions();
  const createMutation = useCreateAgreementLine();
  const updateMutation = useUpdateAgreementLine();
  const deleteMutation = useDeleteAgreementLine();
  const reorderMutation = useReorderAgreementLines();
  const catPlusMutation = useAddCatPlusLines();

  const lines = linesQuery.data ?? [];
  const products = productsQuery.data ?? [];
  const isFormOpen = editing !== null;
  const isCreate = editing === 'new';
  const mutation = isCreate ? createMutation : updateMutation;
  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError = mutation.isError
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Failed to save line.'
    : null;

  const openCreate = (): void => {
    setForm(EMPTY_LINE);
    setEditing('new');
    createMutation.reset();
    updateMutation.reset();
  };

  const openEdit = (line: AgreementLine): void => {
    setForm(toFormState(line));
    setEditing(line.id);
    createMutation.reset();
    updateMutation.reset();
  };

  const closeForm = (): void => {
    setEditing(null);
  };

  const handleProductChange = (productId: string): void => {
    const product: Product | undefined = products.find((p) => p.id === productId);
    setForm((f) => ({
      ...f,
      productId,
      description: product ? product.name : f.description,
      unitPrice: product ? product.unitPrice : f.unitPrice,
    }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const base = {
      productId: form.productId ? form.productId : null,
      description: form.description.trim(),
      billingFrequency: form.billingFrequency,
      quantity: form.quantity.trim() ? form.quantity.trim() : '1',
      unitPrice: form.unitPrice.trim() ? form.unitPrice.trim() : '0',
      startDate: form.startDate ? form.startDate : undefined,
      endDate: form.endDate ? form.endDate : null,
    };

    if (isCreate) {
      const payload: CreateAgreementLineInput = base;
      createMutation.mutate({ agreementId, input: payload }, { onSuccess: closeForm });
    } else if (editing) {
      updateMutation.mutate(
        { agreementId, lineId: editing, input: base },
        { onSuccess: closeForm },
      );
    }
  };

  const handleDelete = (lineId: string): void => {
    deleteMutation.mutate({ agreementId, lineId }, { onSuccess: () => setConfirmId(null) });
  };

  /** Moves a line up/down and persists the new order so the invoice follows it. */
  const moveLine = (index: number, direction: -1 | 1): void => {
    const target = index + direction;
    if (target < 0 || target >= lines.length) return;
    const orderedIds = lines.map((l) => l.id);
    [orderedIds[index], orderedIds[target]] = [orderedIds[target]!, orderedIds[index]!];
    reorderMutation.mutate({ agreementId, orderedIds });
  };

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
        <span className="text-sm font-medium text-slate-700">Lines</span>
        {canEdit && !isFormOpen ? (
          <div className="flex items-center gap-2">
            {showCatPlus ? (
              <Button
                variant="secondary"
                onClick={() => catPlusMutation.mutate(agreementId)}
                isLoading={catPlusMutation.isPending}
                className="px-2.5 py-1 text-xs"
              >
                Add Cat+ lines
              </Button>
            ) : null}
            <Button variant="secondary" onClick={openCreate} className="px-2.5 py-1 text-xs">
              Add line
            </Button>
          </div>
        ) : null}
      </div>

      {catPlusMutation.data ? (
        <p className="border-b border-slate-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          Added {catPlusMutation.data.created} Cat+ line
          {catPlusMutation.data.created === 1 ? '' : 's'}
          {catPlusMutation.data.skipped > 0
            ? ` · ${catPlusMutation.data.skipped} already on the agreement`
            : ''}
          {catPlusMutation.data.missingSkus.length > 0
            ? ` · missing from the product catalog: ${catPlusMutation.data.missingSkus.join(', ')}`
            : ''}
          .
        </p>
      ) : null}
      {catPlusMutation.isError ? (
        <p className="border-b border-slate-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {catPlusMutation.error instanceof ApiError
            ? catPlusMutation.error.message
            : 'Failed to add the Cat+ lines.'}
        </p>
      ) : null}

      <div className="divide-y divide-slate-100">
        {linesQuery.isLoading ? (
          <p className="px-3 py-2 text-sm text-slate-500">Loading lines…</p>
        ) : lines.length === 0 && !isFormOpen ? (
          <p className="px-3 py-2 text-sm text-slate-500">No lines yet.</p>
        ) : (
          lines.map((line, index) => (
            <div key={line.id} className="flex items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-slate-900">
                    {line.description}
                  </span>
                  <Badge tone="blue">{FREQUENCY_LABELS[line.billingFrequency]}</Badge>
                </div>
                <div className="truncate text-xs text-slate-500">
                  {line.quantity} × {currency.format(Number(line.unitPrice))} · from{' '}
                  {formatDate(line.startDate)}
                  {line.endDate ? ` · ends ${formatDate(line.endDate)}` : ''}
                  {line.billedThroughDate
                    ? ` · billed thru ${formatDate(line.billedThroughDate)}`
                    : ''}
                </div>
              </div>
              {canEdit ? (
                <div className="flex shrink-0 items-center gap-1">
                  {lines.length > 1 ? (
                    <div className="flex flex-col">
                      <button
                        type="button"
                        aria-label="Move line up"
                        className="px-1 text-xs leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        disabled={index === 0 || reorderMutation.isPending}
                        onClick={() => moveLine(index, -1)}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label="Move line down"
                        className="px-1 text-xs leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        disabled={index === lines.length - 1 || reorderMutation.isPending}
                        onClick={() => moveLine(index, 1)}
                      >
                        ▼
                      </button>
                    </div>
                  ) : null}
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    onClick={() => openEdit(line)}
                  >
                    Edit
                  </Button>
                  {confirmId === line.id ? (
                    <>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                        onClick={() => handleDelete(line.id)}
                        isLoading={deleteMutation.isPending}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => setConfirmId(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                      onClick={() => setConfirmId(line.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>

      {isFormOpen ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 border-t border-slate-200 p-3">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}
          <ProductPicker
            label="Product"
            products={products}
            value={form.productId}
            emptyLabel="No product (ad-hoc)"
            error={fieldErrors.productId}
            onChange={handleProductChange}
          />
          <TextField
            label="Description"
            required
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            error={fieldErrors.description}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Quantity"
              type="number"
              min={0}
              step="0.01"
              required
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              error={fieldErrors.quantity}
            />
            <TextField
              label="Unit price"
              type="number"
              min={0}
              step="0.01"
              required
              value={form.unitPrice}
              onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
              error={fieldErrors.unitPrice}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Billing frequency"
              value={form.billingFrequency}
              options={FREQUENCY_OPTIONS}
              error={fieldErrors.billingFrequency}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  billingFrequency: e.target.value as BillingFrequency,
                }))
              }
            />
            <TextField
              label="Start date"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              error={fieldErrors.startDate}
            />
          </div>
          <TextField
            label="End date"
            type="date"
            value={form.endDate}
            onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
            error={fieldErrors.endDate}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={closeForm}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={mutation.isPending}>
              {isCreate ? 'Add line' : 'Save line'}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
